import { Button, type ButtonProps } from "@cmp-src/primitives/button";
import { cn } from "@gettactic/helpers";

export interface ButtonInGroupProps extends ButtonProps {
  /**
   * Set when the button is the only one in its group. It then keeps the first-button shape
   * (rounded left, flat right), so it can sit flush against an input on its right: `ArrowsGroup`
   * uses it for a lone "previous" arrow in `CalendarInput`.
   * @default false
   */
  isAlone?: boolean;
}

/**
 * A button inside a `ButtonGroup`. It is always an `outline`, `sm` `Button`: the group
 * draws the joined borders and dividers, so `variant` and `size` cannot be changed.
 */
export const ButtonInGroup = ({
  children,
  isAlone,
  ...props
}: ButtonInGroupProps) => {
  return (
    <Button
      {...props}
      className={cn(
        "h-10",
        "mx-0 gap-0",
        "bg-background",
        "p-1",
        "border-input border-y md:border-x-0",
        "rounded-v2",
        "first:rounded-v2-l first:rounded-r-none first:border-r-0 first:border-l",
        !isAlone &&
          "last:rounded-v2-r last:rounded-l-none last:border-r-1 last:border-l-0",
        "not-last:border-r-0 not-first:border-l-0",
        "group/btn-in-group",
        !props.disabled && "hover:border-neutral-200 hover:bg-background",
        props.className
      )}
      variant="outline"
      size="sm"
    >
      <span
        className={cn(
          "flex h-full w-full items-center justify-center",
          "flex items-center justify-center",
          "bg-transparent transition-colors",
          "rounded-lg",
          !props.disabled && "group-hover/btn-in-group:bg-neutral-50"
        )}
      >
        {children}
      </span>
    </Button>
  );
};
